import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { DataId } from "../../general";

import { VALIDATION } from "../../constants/validation";
import { OptionLabelProvider } from "../DataDropdown";
import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { Input } from "./Input";
import { MultiSelectDataDropdownDataSource } from "./MultiSelectDataDropdown";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `Field` component properties.
 */
export interface FieldProps<TRow extends object> extends FieldBaseProps<ReadonlyArray<DataId> | null>, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The data source to use to retrieve dropdown items.
   */
  readonly dataSource: MultiSelectDataDropdownDataSource<TRow>;

  /**
   * The function to call returning the label for a particular row.
   */
  readonly optionLabelProvider: OptionLabelProvider<TRow>;

  /**
   * The field to search against. If remote, corresponds to a field alias. If not, corresponds to a dot path to the field on the row.
   */
  readonly searchField?: string;

  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;
}

/**
 * A React Hook Form compatible `MultiSelectDataDropdown` component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field = <TRow extends object>(props: FieldProps<TRow>) => {
  const { name, validator, required = false, defaultValue = null } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: readonly (string | number)[], formValues: FieldValues) => {
      if (required && (value === null || value === undefined || value.length === 0)) return VALIDATION.REQUIRED_AT_LEAST_ONE;

      return validator ? validator(value, formValues) : true;
    },
    [required, validator],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [validateHandler, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...props}
          optionLabelProvider={props.optionLabelProvider as OptionLabelProvider<object>}
          ref={ref}
          value={value}
          onChange={onChange}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
